import React, { useState, useContext, useEffect, Fragment } from "react";
import styled from "styled-components";
import Grid from "./components/layout/grid";
import Card from "./components/layout/card";
import { AppContext } from "./app";
import { LEGAL_POLICY_LINKS } from "../utils/legal_policy_links";
import Input from "./components/base_ui/inputs/input";
import { FormControl } from "./components/base_ui/inputs/form_control";
import { Button } from "./components/base_ui/inputs/button";
import { CenteredLoadingSpinner } from "./components/base_ui/progress_and_validation/spinner/centered_loading_spinner";
import { HeadingSmall, ParagraphMedium } from "baseui/typography";
import { Theme } from "baseui/theme";
import { KIND } from "baseui/button";
import { Enterprise } from "@carbon/icons-react";
import * as yup from "yup";
import Select from "./components/base_ui/inputs/select";
import SsoButton from "./sso_button";
import { BlockOverrides } from "baseui/block";
import { useLocation, useNavigate } from "react-router-dom";
import useForm from "./shared/use_form";
import { captureError } from "../utils/capture_error";

document.title = "Sign in";

export const LoginPage = styled.div`
  padding-top: ${({ theme }) => theme.deprecatedSpacing.layout.three};
  min-height: 100vh;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: center;
  background-color: ${({ theme }) => theme.deprecatedColors.background};
  background-repeat: no-repeat;
  background-size: contain;
  background-position: top;
  background-image: url(/images/participant_dashboard/background_textures/1-1200w.jpg);

  @media ${({ theme }) => theme.deprecatedBreakpoints.small} {
    background-image: url(/images/participant_dashboard/background_textures/1-600w.jpg);
    justify-content: flex-start;
  }
`;

export const ImageBlock = styled(Card)`
  background-color: ${({ theme }) => theme.colors.white};
  grid-column: 2 / 6;
  grid-row: 1 / 2;
  padding: ${({ theme }) => theme.sizing.scale1000};
  display: flex;
  flex-direction: column;
  justify-content: space-around;

  @media ${({ theme }) => theme.deprecatedBreakpoints.large} {
    grid-column: span 3;
  }

  @media ${({ theme }) => theme.deprecatedBreakpoints.small} {
    grid-column: 1 / -1;
    grid-row: 1 / 2;
    height: 60px;
    padding: ${({ theme }) => theme.deprecatedSpacing.component.six};
    flex-direction: row;
  }

  .image,
  .schoolLogo {
    width: 100%;
    max-height: 300px;
    object-fit: contain;

    @media ${({ theme }) => theme.deprecatedBreakpoints.small} {
      max-width: 40%;
      max-height: 100%;
      width: auto;
    }
  }
`;

export const LoginContainer = styled.div`
  min-width: 375px;

  @media ${({ theme }) => theme.deprecatedBreakpoints.medium} {
    min-width: 0px;
  }
`;

export const SsoLoginContainer = styled.div`
  min-width: 375px;
  max-width: 300px;
  min-height: 460px;

  @media ${({ theme }) => theme.deprecatedBreakpoints.medium} {
    min-width: 0px;
  }
`;

export const LoginBlock = styled(Card)`
  background-color: ${({ theme }) => theme.colors.white};
  grid-column: 6 / 12;
  grid-row: 1 / 2;
  padding: ${({ theme }) => theme.sizing.scale1000};
  display: flex;
  flex-direction: column;
  align-items: center;

  @media ${({ theme }) => theme.deprecatedBreakpoints.large} {
    grid-column: span 5;
  }

  @media ${({ theme }) => theme.deprecatedBreakpoints.small} {
    grid-column: 1 / -1;
    grid-row: 2 / 3;
    padding: ${({ theme }) => theme.sizing.scale600};
  }

  .form {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin: 0;
    width: 100%;
  }
`;

const ExtraInfo = styled.div`
  padding: ${({ theme }) => theme.sizing.scale500};
  grid-column: 6 / 12;
  grid-row: 2 / 3;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-around;

  @media ${({ theme }) => theme.deprecatedBreakpoints.large} {
    grid-column: 4 / -1;
  }

  @media ${({ theme }) => theme.deprecatedBreakpoints.small} {
    grid-column: 1 / -1;
    grid-row: 3 / 4;
  }
`;

const ForgotPasswordContainer = styled.div`
  text-align: right;
  margin-top: 0;
  margin-bottom: ${({ theme }) => theme.sizing.scale800};
  width: 100%;
`;

const CannotFindInstitutionContainer = styled.div`
  text-align: center;
  line-height: ${({ theme }) => theme.sizing.scale700};
  margin-top: ${({ theme }) => theme.sizing.scale1000};
  width: 100%;
`;

const StyledSsoButton = styled(SsoButton)`
  margin-top: ${({ theme }) => theme.sizing.scale200};
`;

const Link = styled.a`
  color: ${({ theme }) => theme.colors.linkText};
  font-family: ${({ theme }) => theme.typography.ParagraphSmall.fontFamily};

  &:visited {
    color: ${({ theme }) => theme.colors.linkVisited};
  }
  &:hover {
    color: ${({ theme }) => theme.colors.linkHover};
  }
  &:focus {
    outline-color: ${({ theme }) => theme.colors.accent};
  }
`;

const ExtraInfoLink = styled(Link)`
  color: ${({ theme }) => theme.colors.contentPrimary};
  padding-left: ${({ theme }) => theme.sizing.scale200};
  padding-right: ${({ theme }) => theme.sizing.scale200};

  &:visited {
    color: ${({ theme }) => theme.colors.contentPrimary};
  }
`;

const Separator = styled.div`
  width: 100%;
  height: 10px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderOpaque};
  text-align: center;
  margin-top: ${({ theme }) => theme.sizing.scale500};
  margin-bottom: ${({ theme }) => theme.sizing.scale600};
`;

const SeparatorText = styled.span`
  text-transform: uppercase;
  background-color: ${({ theme }) => theme.colors.white};
  line-height: ${({ theme }) => theme.typography.LabelMedium.lineHeight};
  font-weight: ${({ theme }) => theme.typography.LabelMedium.fontWeight};
  font-size: ${({ theme }) => theme.typography.LabelMedium.fontSize};
  color: ${({ theme }) => theme.colors.contentTertiary};
  padding-left: ${({ theme }) => theme.sizing.scale300};
  padding-right: ${({ theme }) => theme.sizing.scale300};
`;

const headingOverrides: BlockOverrides = {
  Block: {
    style: ({ $theme }: { $theme: Theme }) => ({
      marginTop: 0,
      marginBottom: $theme.sizing.scale500,
    }),
  },
};

const paragraphOverrides: BlockOverrides = {
  Block: {
    style: ({ $theme }: { $theme: Theme }) => ({
      marginTop: 0,
      color: $theme.colors.contentTertiary,
    }),
  },
};

const defaultFormSchema = yup.object().shape({
  email: yup.string().required("Email is required.").email("Invalid email format."),
  password: yup.string().required("Password is required."),
});

const ssoFormSchema = yup.object().shape({
  id: yup.string().required("Please select an institution to proceed."),
});

export default function Login({ onLogin }: { onLogin: () => void }) {
  const { signIn, school, csrfToken } = useContext(AppContext);
  const [isSigningIn, setIsSigningIn] = useState<boolean>(false);
  const location = useLocation();
  const navigate = useNavigate();
  const {
    form: defaultForm,
    validateForm: validateDefaultForm,
    setErrors: setDefaultFormErrors,
  } = useForm(
    { email: "", password: "" } as { email: string; password: string },
    defaultFormSchema,
    true,
  );

  const { form: ssoForm, validateForm: validateSsoForm } = useForm(
    { id: "" } as { id: string | number | undefined },
    ssoFormSchema,
    true,
  );

  const [showSsoLoginOption, setShowSsoLoginOption] = useState(false);
  const [ssoEnabledSchools, setSsoEnabledSchools] = useState<
    {
      id: string;
      name: string;
      label: string;
      sso_provider_identifier: string;
    }[]
  >([]);

  useEffect(() => {
    async function init() {
      const response = await fetch("/sso_enabled_schools", {
        method: "GET",
        headers: {
          "X-CSRF-Param": "authenticity_token",
          "X-CSRF-Token": csrfToken,
        },
      });

      const ssoEnabledSchoolsResponse = await response.json();

      const schools = ssoEnabledSchoolsResponse.schools.map((school: { name: string }) => ({
        ...school,
        label: school.name,
      }));

      setSsoEnabledSchools(schools);

      const schoolsWithoutSsoProviderIdentifierPresent = schools.filter(
        (school: { sso_provider_identifier: string }) =>
          !school.sso_provider_identifier || school.sso_provider_identifier === "",
      );

      if (schoolsWithoutSsoProviderIdentifierPresent.length > 0) {
        captureError(
          `SSO Configuration Error: The following institutions have SSO enabled but do not have an sso_provider_identifier present: ${schoolsWithoutSsoProviderIdentifierPresent
            .map((school: { id: string }) => school.id)
            .join(", ")}`,
        );
      }
    }

    init();
  }, [csrfToken]);

  const ssoBrand = school?.ssoBrand ? school.ssoBrand : "Institutional Credentials";

  const schoolIsSsoSchool = school && school?.isSsoEnabled && school?.ssoConnection;
  const allowForSsoTesting = school && school?.isSsoTestingEnabled;
  const showSsoLoginButton = !school || schoolIsSsoSchool || allowForSsoTesting;

  return (
    <LoginPage>
      <Grid>
        <ImageBlock animateOnHover={false}>
          {school?.logo && <img className="schoolLogo" src={school.logo} alt={`${school.name}`} />}
          <img className="image" src="/images/mc_logo_full.png" alt="Mentor Collective" />
        </ImageBlock>
        <LoginBlock>
          {!showSsoLoginOption ? (
            // username/password login
            <LoginContainer>
              <HeadingSmall overrides={headingOverrides}>Sign In</HeadingSmall>
              <ParagraphMedium overrides={paragraphOverrides}>
                Welcome back! Please enter your details.
              </ParagraphMedium>
              <form
                className="form"
                noValidate={true}
                onSubmit={async (event) => {
                  event.preventDefault();
                  try {
                    await validateDefaultForm();
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                  } catch (error) {
                    return;
                  }

                  setIsSigningIn(true);
                  try {
                    await signIn({
                      email: defaultForm.email.value,
                      password: defaultForm.password.value,
                    });
                    onLogin();
                    if (location?.state?.previousLocation?.pathname) {
                      navigate(
                        `${location.state.previousLocation.pathname}${
                          location.state.previousLocation?.search ?? ""
                        }`,
                      );
                    }
                  } catch {
                    setDefaultFormErrors([
                      { path: "password", message: "Email and/or password is incorrect." },
                    ]);
                  }
                  setIsSigningIn(false);
                }}
              >
                <FormControl
                  label="Email"
                  htmlFor="email-input"
                  error={defaultForm.email.errors.join(", ")}
                  overrides={{ Caption: { style: { marginBottom: 0 } } }}
                >
                  <Input
                    id="email-input"
                    type="email"
                    aria-label="Email field"
                    value={defaultForm.email.value}
                    error={!!defaultForm.email.errors.length}
                    onChange={({ currentTarget: { value } }) => defaultForm.email.set(value)}
                    onBlur={({ currentTarget: { value } }) => {
                      defaultForm.email.setTouched(true);
                      defaultForm.email.set(value);
                    }}
                    dataTest={"sign-in-email-input"}
                    placeholder="Enter your email"
                  />
                </FormControl>
                <FormControl
                  overrides={{ Caption: { style: { marginBottom: 0 } } }}
                  label="Password"
                  htmlFor="password-input"
                  error={defaultForm.password.errors.join(", ")}
                >
                  <Input
                    id="password-input"
                    type="password"
                    aria-label="Password field"
                    value={defaultForm.password.value}
                    error={!!defaultForm.password.errors.length}
                    onChange={({ currentTarget: { value } }) => defaultForm.password.set(value)}
                    onBlur={({ currentTarget: { value } }) => defaultForm.password.set(value)}
                    onFocus={() => defaultForm.password.setTouched(true)}
                    dataTest={"sign-in-password-input"}
                    placeholder="Enter your password"
                  />
                </FormControl>

                <ForgotPasswordContainer>
                  <Link href="/password_reset">Forgot Password?</Link>
                </ForgotPasswordContainer>

                <Button
                  isStretched={true}
                  type="submit"
                  data-test="sign-in-submit-button"
                  data-analytics-id={school ? "subdomain-sign-in" : "domain-sign-in"}
                  disabled={isSigningIn}
                >
                  Sign In
                </Button>
                {isSigningIn && <CenteredLoadingSpinner />}
              </form>
              {showSsoLoginButton && (
                <Fragment>
                  <Separator>
                    <SeparatorText>OR</SeparatorText>
                  </Separator>

                  {!school && (
                    <Button
                      isStretched={true}
                      onClick={(event) => {
                        event.preventDefault();
                        setShowSsoLoginOption(true);
                      }}
                      buttonType={KIND.secondary}
                      leftIcon={<Enterprise size={32} />}
                      data-test="sso-school-selection-button"
                      data-analytics-id="domain-sign-in-with-institution-credentials"
                    >
                      Sign In With {ssoBrand}
                    </Button>
                  )}

                  {(schoolIsSsoSchool || allowForSsoTesting) && (
                    <SsoButton
                      buttonType={KIND.secondary}
                      connection={school.ssoConnection}
                      csrfToken={csrfToken}
                      dataAnalyticsId="subdomain-sign-in-with-institution-credentials"
                      buttonText={`Sign In With ${ssoBrand}`}
                    />
                  )}
                </Fragment>
              )}
            </LoginContainer>
          ) : (
            // sso login
            <SsoLoginContainer>
              <HeadingSmall overrides={headingOverrides}>Select Your Institution</HeadingSmall>
              <ParagraphMedium overrides={paragraphOverrides}>
                If your institution has partnered with Mentor Collective to provide single sign on
                (SSO), search for your institution below.
              </ParagraphMedium>
              <StyledSsoButton
                buttonText="Continue"
                showIcon={false}
                connection={
                  ssoEnabledSchools.find((institution) => institution.id === ssoForm.id.value)
                    ?.sso_provider_identifier
                }
                className="form"
                csrfToken={csrfToken}
                onSubmit={async (event) => {
                  try {
                    await validateSsoForm();
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                  } catch (error) {
                    event.preventDefault();
                    return;
                  }
                }}
              >
                <FormControl
                  label="Institution SSO Options"
                  htmlFor="sso-instutution-select"
                  error={ssoForm.id.errors.join(", ")}
                >
                  <Select
                    id="sso-instutution-select"
                    value={ssoForm.id.value !== "" ? [{ id: ssoForm.id.value }] : undefined}
                    onChange={(params) => ssoForm.id.set(params.value[0].id)}
                    onBlur={() => ssoForm.id.set(ssoForm.id.value)}
                    onFocus={() => ssoForm.id.setTouched(true)}
                    error={!!ssoForm.id.errors.length}
                    aria-label="SSO institution select field"
                    options={ssoEnabledSchools}
                    dataTest={"sso-institution-select"}
                    placeholder="Search here..."
                    disabled={!ssoEnabledSchools.length}
                    searchable
                  />
                </FormControl>

                {isSigningIn && <CenteredLoadingSpinner />}
              </StyledSsoButton>
              <CannotFindInstitutionContainer>
                <ParagraphMedium>
                  Can't find your institution? <Link href="/">Go back to sign in</Link>
                </ParagraphMedium>
              </CannotFindInstitutionContainer>
            </SsoLoginContainer>
          )}
        </LoginBlock>
        <ExtraInfo>
          <ExtraInfoLink
            target="_blank"
            rel="noopener noreferrer"
            href={"https://mentorcollective.org"}
            aria-label="What is Mentor Collective, opens in a new tab"
          >
            What is Mentor Collective?
          </ExtraInfoLink>
          <ExtraInfoLink
            target="_blank"
            rel="noopener noreferrer"
            href={LEGAL_POLICY_LINKS.PRIVACY_POLICY_URL_PDF}
            aria-label="Privacy Policy, opens in a new tab"
          >
            Privacy Policy
          </ExtraInfoLink>
          <ExtraInfoLink
            target="_blank"
            rel="noopener noreferrer"
            href={LEGAL_POLICY_LINKS.TERMS_OF_USE_URL_PDF}
            aria-label="Terms of Use, opens in a new tab"
          >
            Terms Of Use
          </ExtraInfoLink>
        </ExtraInfo>
      </Grid>
    </LoginPage>
  );
}
